import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { ChipList } from '../attachments/ChipList'
import { UploadCloudIcon } from '../components/icons'
import { useFileIntake } from '../hooks/useFileIntake'
import { armVoiceReply } from '../lib/speech'
import { ClipIcon, ImageIcon, TemporaryChatIcon } from '../components/icons'
import { CallButton } from './CallButton'
import { MicButton } from './MicButton'
import { PromptChips } from './PromptChips'
import { ACCEPT } from '../lib/attachments'

/**
 * Mesaj yazma alani.
 *
 * ## Draft konusma BAZINDA saklaniyor
 *
 * Kullanici yazmaya baslayip baska bir sohbete gecip donunce yazdigini
 * bulmali. `localStorage`, konusma id'siyle anahtarlanmis: sekme kapanip
 * acilsa da duruyor. Bunu bellekte tutmak, sayfa yenilenince kaybetmek
 * demek olurdu - ve kullanici uzun bir mesaj yaziyorken bu gercek bir kayip.
 *
 * ## Ekler GONDERIRKEN DEGIL, ALIRKEN yukleniyor
 *
 * `useFileIntake` uc yolu (secici, yapistirma, surukleme) birlestiriyor ve
 * yukleme HEMEN basliyor. Kazanc: belge cikarimi gecikmesi kritik yoldan
 * cikiyor - kullanici yazmaya devam ederken "okunuyor…" goruyor, donmus bir
 * gonder butonuna bakmak yerine.
 *
 * Gonder butonu TUM chip'ler hazir olana kadar kapali: yarim yuklenmis bir
 * eki gondermek, modele hic ulasmayan bir dosya demek olurdu.
 *
 * ## `Stop` butonu bir NEZAKET DEGIL, adillik ozelligi
 *
 * Tek GPU'da bir kullanicinin kacak uzun yaniti herkesi blokluyor. Durdurmak
 * slotu bosaltiyor.
 */

const DRAFT_PREFIX = 'hedefai:draft:'
const FAST_REPLY_KEY = 'hedefai:fastReply'
const IMAGE_OPTIONS_KEY = 'hedefai:imageOptions'

/**
 * Gorsel uretme secenekleri (sunucuda `generate_image` araci).
 *
 * `auto`: boyutu model istemden cikariyor (poster -> dikey, manzara -> yatay).
 * Kalite: `fast` FLUX.2 (~1 dk), `high` Z-Image (~3-4 dk, ayrintiya daha sadik).
 * Hizli-yanit anahtari gibi global ve kalici - her mesajda yeniden secilmemeli.
 */
export type ImageSize = 'auto' | 'square' | 'landscape' | 'portrait'
export type ImageQuality = 'fast' | 'high'
export interface ImageOptions {
  size: ImageSize
  quality: ImageQuality
}

export const DEFAULT_IMAGE_OPTIONS: ImageOptions = { size: 'auto', quality: 'fast' }

const IMAGE_SIZE_LABELS = {
  auto: 'chat.composer.imageSizeAuto',
  square: 'chat.composer.imageSizeSquare',
  landscape: 'chat.composer.imageSizeLandscape',
  portrait: 'chat.composer.imageSizePortrait',
} as const

const IMAGE_QUALITY_LABELS = {
  fast: 'chat.composer.imageQualityFast',
  high: 'chat.composer.imageQualityHigh',
} as const

export function Composer({
  conversationId,
  busy,
  onSend,
  onStop,
  showSuggestions,
  temporary,
  onToggleTemporary,
}: {
  /** `null` = yeni sohbet. */
  conversationId: string | null
  busy: boolean
  onSend(
    content: string,
    attachmentIds: string[],
    model: string | null,
    imageOptions: ImageOptions,
  ): void
  onStop?(): void
  /** Bos sohbet ekraninda kayitli prompt onerileri gosterilsin mi. */
  showSuggestions?: boolean
  /** Bu YENI sohbet gecici mi acilacak (yalnizca `conversationId` null iken). */
  temporary?: boolean
  /**
   * Gecici anahtarini cevirir. YALNIZCA yeni sohbette veriliyor: var olan
   * bir sohbet gecici yapilamiyor ve gecici bir sohbet kaliciya
   * cevrilemiyor - ikisi de kullanicinin "bu konusma kaydedilmeyecek"
   * beklentisini sonradan degistirmek olurdu.
   */
  onToggleTemporary?(): void
}) {
  const { t } = useTranslation()
  const key = DRAFT_PREFIX + (conversationId ?? 'new')
  const [draft, setDraft] = useState('')
  // Kullanici KENDISI kapatana kadar acik kalmali - tek bir mesaj gonderince
  // sessizce sonmemeli. `localStorage`da tutuluyor cunku yeni bir sohbetin
  // ilk mesaji gonderilince `conversationId` `null`dan gercek id'ye gecip
  // rota degisiyor ve bu `Composer`i YENIDEN MOUNT ediyor - sade bir
  // `useState` bu geciste sessizce sifirlanirdi (gozlenen gercek hata).
  const [fast, setFast] = useState(readFastReply)
  const [imageOptions, setImageOptions] = useState(readImageOptions)
  const [imageOpen, setImageOpen] = useState(false)
  const area = useRef<HTMLTextAreaElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  const intake = useFileIntake()

  // Konusma degistiginde o konusmanin draft'ini yukle.
  useEffect(() => {
    setDraft(readDraft(key))
  }, [key])

  // Yazarken sakla. Her tusa basista `localStorage` yazmak (senkron I/O)
  // uzun mesajlarda hissedilir; kisa bir gecikmeyle topluyoruz.
  useEffect(() => {
    const timer = setTimeout(() => writeDraft(key, draft), 300)
    return () => clearTimeout(timer)
  }, [key, draft])

  // Yazi alani icerige gore BUYUYOR (tek satirdan baslayip ~8 satira kadar);
  // sabit iki satirlik bir kutu kisa sorularda bos, uzun metinlerde dar kaliyordu.
  useEffect(() => {
    const element = area.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight, 220)}px`
  }, [draft])

  const canSend = (draft.trim().length > 0 || intake.ids.length > 0) && intake.ready && !busy
  const imageCustomized =
    imageOptions.size !== DEFAULT_IMAGE_OPTIONS.size ||
    imageOptions.quality !== DEFAULT_IMAGE_OPTIONS.quality

  function updateImageOptions(patch: Partial<ImageOptions>) {
    setImageOptions((current) => {
      const next = { ...current, ...patch }
      writeImageOptions(next)
      return next
    })
  }

  function send() {
    if (!canSend) return
    onSend(draft.trim(), intake.ids, fast ? 'fast' : null, imageOptions)
    setDraft('')
    intake.clear()
    // Gonderildi: draft'i HEMEN sil, gecikmeyi bekleme. Aksi halde
    // zamanlayici eski metni geri yazabilir.
    writeDraft(key, '')
    area.current?.focus()
  }

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault()
        send()
      }}
    >
      {/*
        Surukleme SEKMENIN HERHANGI bir yerinde algilaniyor (bkz.
        `useFileIntake` - dinleyiciler `window` seviyesinde), yalnizca bu
        formun uzerinde degil. Gosterge de buna gore TUM ekrani kaplayan bir
        overlay - `document.body`ye portal'lanmis, composer'in kucuk kutusuna
        hapsolmus degil.
      */}
      {intake.dragging &&
        createPortal(
          <div className="drop-overlay" aria-hidden="true">
            <div className="drop-hint">
              <span className="drop-hint-icon">
                <UploadCloudIcon size={36} />
              </span>
              <strong className="drop-hint-title">{t('chat.composer.dropHint')}</strong>
              <span className="drop-hint-sub">{t('chat.composer.dropHintSub')}</span>
            </div>
          </div>,
          document.body,
        )}

      {showSuggestions && !draft && <PromptChips onPick={setDraft} />}

      <ChipList chips={intake.chips} onRemove={intake.remove} onRetry={intake.retry} />

      {!intake.ready && intake.chips.length > 0 && (
        <p className="composer-note" role="status">
          {t('chat.composer.attachmentsPreparing')}
        </p>
      )}

      {imageOpen && (
        <div
          id="composer-image-options"
          className="composer-image-options"
          role="group"
          aria-label={t('chat.composer.imageOptions')}
        >
          <label>
            <span>{t('chat.composer.imageSize')}</span>
            <select
              value={imageOptions.size}
              onChange={(event) => updateImageOptions({ size: event.target.value as ImageSize })}
            >
              {(Object.keys(IMAGE_SIZE_LABELS) as ImageSize[]).map((size) => (
                <option key={size} value={size}>
                  {t(IMAGE_SIZE_LABELS[size])}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t('chat.composer.imageQuality')}</span>
            <select
              value={imageOptions.quality}
              onChange={(event) =>
                updateImageOptions({ quality: event.target.value as ImageQuality })
              }
            >
              {(Object.keys(IMAGE_QUALITY_LABELS) as ImageQuality[]).map((quality) => (
                <option key={quality} value={quality}>
                  {t(IMAGE_QUALITY_LABELS[quality])}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/*
        TEK kutu: yazi alani ustte, araclar altta tek satirda. Onceki halde
        dort ayri cerceveli dugme, genis bir yazi kutusu ve uzaktaki
        "Gonder" ayni satira diziliyordu; genis ekranda dagink, telefonda
        sikisik gorunuyordu (kullanici geri bildirimi).
      */}
      <div className="composer-box" onClick={(event) => {
        // Kutunun bos bir yerine tiklamak yaziya odaklansin (dugmeler haric).
        if (event.target === event.currentTarget) area.current?.focus()
      }}>
        <input
          ref={picker}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          onChange={(event) => {
            if (event.target.files) intake.add(Array.from(event.target.files))
            // Ayni dosyayi ikinci kez secmek `change` tetiklemiyor; degeri
            // sifirlamak bunu duzeltiyor.
            event.target.value = ''
          }}
        />

        <textarea
          ref={area}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter gonderir, Shift+Enter yeni satir. (Ayardan takas
            // edilebilir olmasi P6'da.) `isComposing` kontrolu SART: IME ile
            // yazan kullanicida Enter aday secimi yapiyor, gonderme degil.
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              send()
            }
          }}
          placeholder={t('chat.composer.placeholder')}
          rows={1}
          aria-label={t('chat.composer.messageLabel')}
        />

        <div className="composer-toolbar">
          <div className="composer-tools">
            <button
              type="button"
              className="icon-btn"
              onClick={() => picker.current?.click()}
              aria-label={t('chat.composer.attach')}
              title={t('chat.composer.attach')}
            >
              <ClipIcon />
            </button>

            {/*
              Gorsel secenekleri: yalnizca model `generate_image` cagirirsa
              kullaniliyor. Varsayilandan farkli bir secim varken dugme vurgulu -
              kullanici neden yatay gorsel geldigini unutmasin.
            */}
            <button
              type="button"
              className={imageCustomized ? 'icon-btn image-on' : 'icon-btn'}
              aria-expanded={imageOpen}
              aria-controls="composer-image-options"
              aria-label={t('chat.composer.imageOptions')}
              title={t('chat.composer.imageOptions')}
              onClick={() => setImageOpen((current) => !current)}
            >
              <ImageIcon />
            </button>

            {/*
              Hizli-yanit anahtari - kullanici kapatana kadar acik kalir (bkz.
              `fast` state'inin ustundeki yorum). Acikken "fast" (Qwen3.5-4B),
              kapaliyken sunucu varsayilani "reason" (Normal, Qwen3.5-9B) kullanilir.
              Iki model de resim gorebildigi icin tercih resimli mesajlarda da
              aynen uygulanir (bkz. `model_manager.py::route`). Gorunur "Hizli"
              yazisi durumu ikonsuz da okunur kiliyor; erisilebilir ad modeli de
              soyluyor.
            */}
            <button
              type="button"
              className={fast ? 'composer-pill fast-on' : 'composer-pill'}
              aria-pressed={fast}
              aria-label={t('chat.composer.fastToggle')}
              title={t('chat.composer.fastToggle')}
              onClick={() =>
                setFast((current) => {
                  const next = !current
                  writeFastReply(next)
                  return next
                })
              }
            >
              <BoltIcon />
              <span>{t('chat.composer.fastShort')}</span>
            </button>

            {/*
              Gecici sohbet anahtari - yalnizca YENI sohbette. Kenar
              cubugundaki dugmeyle ayni isi yapiyor; burada olmasinin sebebi
              kullanicinin karari cogu zaman yazmaya baslarken vermesi.
            */}
            {onToggleTemporary && (
              <button
                type="button"
                className={temporary ? 'composer-pill temporary-on' : 'composer-pill'}
                aria-pressed={Boolean(temporary)}
                aria-label={t('chat.composer.temporaryToggle')}
                title={t('chat.composer.temporaryToggle')}
                onClick={onToggleTemporary}
              >
                <TemporaryChatIcon size={16} />
                <span>{t('chat.composer.temporaryShort')}</span>
              </button>
            )}
          </div>

          <div className="composer-actions">
            {/*
              Sesli gorusme: telefonda konusur gibi, dugmeye basmadan. Model
              yazi kutusundaki secimi izliyor (kullanici karari). Ozellik bu
              sunucuda/tarayicida calisamiyorsa dugme HIC gorunmuyor.
            */}
            <CallButton
              conversationId={conversationId}
              model={fast ? 'fast' : null}
              disabled={busy}
            />
            {/*
              Mikrofon: sunucu STT'yi acmadiysa veya tarayici desteklemiyorsa
              bilesen `null` donduruyor, yani buton HIC gorunmuyor.
              Transkript taslagin UZERINE yazmiyor, sonuna EKLENIYOR - uzerine
              yazmak kullanicinin cumlesini sessizce silmek olurdu.
            */}
            <MicButton
              disabled={busy}
              onText={(text) =>
                setDraft((current) => (current.trim() ? `${current.trim()} ${text}` : text))
              }
              // KUTU BOSKEN sesli mesaj: kayit bitince dogrudan gidiyor ve
              // yanit sesli okunuyor (telefonla konusur gibi). Kutuda metin
              // varsa kullanici yaziyor demektir - o zaman dikte: metnin sonuna
              // ekleniyor, cumlesini sessizce gondermek surpriz olurdu.
              onVoiceMessage={
                draft.trim()
                  ? null
                  : (text, attachmentId) => {
                      armVoiceReply()
                      // Kayit mesajin ILK eki: arayuz onu oynatici olarak,
                      // metni (transkript) altinda gosteriyor.
                      onSend(text, [attachmentId, ...intake.ids], fast ? 'fast' : null, imageOptions)
                      intake.clear()
                    }
              }
            />

            {busy && onStop ? (
              <button
                type="button"
                className="composer-send composer-stop"
                onClick={onStop}
                aria-label={t('chat.composer.stop')}
                title={t('chat.composer.stop')}
              >
                <StopSquareIcon />
              </button>
            ) : (
              <button
                type="submit"
                className="composer-send"
                disabled={!canSend}
                aria-label={t('chat.composer.send')}
                title={t('chat.composer.send')}
              >
                <SendIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 19V5M5.5 11.5 12 5l6.5 6.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StopSquareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" fill="currentColor" />
    </svg>
  )
}

function BoltIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function readImageOptions(): ImageOptions {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(IMAGE_OPTIONS_KEY) ?? 'null',
    ) as Partial<ImageOptions> | null
    return {
      size:
        parsed?.size && parsed.size in IMAGE_SIZE_LABELS ? parsed.size : DEFAULT_IMAGE_OPTIONS.size,
      quality:
        parsed?.quality && parsed.quality in IMAGE_QUALITY_LABELS
          ? parsed.quality
          : DEFAULT_IMAGE_OPTIONS.quality,
    }
  } catch {
    return DEFAULT_IMAGE_OPTIONS
  }
}

function writeImageOptions(value: ImageOptions): void {
  try {
    localStorage.setItem(IMAGE_OPTIONS_KEY, JSON.stringify(value))
  } catch {
    // Tercih bir kolaylik; secim bu oturumda yine gecerli.
  }
}

function readDraft(key: string): string {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    // Ozel pencere / kota: draft ozelligi kaybolur ama uygulama calisir.
    return ''
  }
}

function writeDraft(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value)
    else localStorage.removeItem(key)
  } catch {
    // Sessizce gecmek DOGRU: draft bir kolaylik, kullanici mesajini
    // gonderebiliyor.
  }
}

// `DRAFT_PREFIX`in aksine konusma bazinda DEGIL - tek, global bir anahtar.
// Kullanici hangi sohbete gecerse gecsin ayni tercih (acik/kapali) gecerli
// kalmali; "until I change it again" tam olarak bunu istiyor.
function readFastReply(): boolean {
  try {
    return localStorage.getItem(FAST_REPLY_KEY) === '1'
  } catch {
    return false
  }
}

function writeFastReply(value: boolean): void {
  try {
    localStorage.setItem(FAST_REPLY_KEY, value ? '1' : '0')
  } catch {
    // Sessizce gec: tercih bir kolaylik, anahtarin kendisi yine calisir -
    // sadece bir sonraki mount'ta hatirlanmaz.
  }
}
